import { AppContextType } from '../types';
import { ProductType } from '../types/products';
import { createContext, useContext } from 'react';
import { UserInfo } from 'hub-http/userInfo';
import { ContactLeadAction } from '../../__generated__/chirp/com/hubspot/marketplace/analytics/rpc/LeadGenerationRpc';

export interface RunLeadGenProps {
  action: ContactLeadAction;
}

// marketplace-ui-tracking depends on marketplace-ui-common, so importing its types here
// would create a circular dependency. These local types are structurally compatible with
// MarketplaceInteraction and Partial<InstallButtonClicked> from marketplace-ui-tracking —
// the full tracking types are still enforced at the useTracking level in marketplace-ui-install.
export interface DetailsPageContextType {
  appContext: AppContextType;
  displayTag?: string;
  iconUrl: string;
  listingId: number;
  listingIdV2: number; // ID from unifiedDetailsDataV2
  previewListingId?: number;
  name: string;
  productType: ProductType;
  published: boolean;
  requiredForInstallListingIds?: number[];
  runLeadGen: (props: RunLeadGenProps) => void;
  trackInteraction: (props: { action: string; [key: string]: unknown }) => void;
  trackInstall: (props: object) => void;
  userInfo?: Partial<UserInfo>;
  isEmbedded?: boolean;
  isBreezeConnector?: boolean;
  isMcpConnector?: boolean;
  isMcpServer?: boolean;
  worksWithBreeze?: boolean;
}

export const DetailsPageContext = createContext<DetailsPageContextType>({
  appContext: 'app',
  displayTag: undefined,
  iconUrl: '',
  listingId: 0,
  listingIdV2: 0, // TODO - deprecate --- this is still needed for sendAdminEmail
  previewListingId: undefined,
  name: '',
  productType: 'APP',
  published: true,
  requiredForInstallListingIds: [],
  runLeadGen: () => {},
  trackInteraction: () => {},
  trackInstall: () => {},
  userInfo: {},
  isEmbedded: false,
  isBreezeConnector: false,
  isMcpConnector: false,
  isMcpServer: false,
  worksWithBreeze: false,
});

export const useDetailsPageContext = () => {
  const context = useContext(DetailsPageContext);

  if (!context) {
    throw new Error(
      'useDetailsPageContext must be used within a DetailsPageContext.Provider'
    );
  }
  return context;
};
